import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { updateSettings } from "../../store/settingsSlice";
import { setTheme } from "../../store/uiSlice";

const SettingsPanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { language, theme } = useSelector((state: RootState) => state.ui);
  const { settings, loading } = useSelector(
    (state: RootState) => state.settings
  );

  const [localSettings, setLocalSettings] = useState({
    endpoint: "",
    pollInterval: 5000,
    defaultZoom: 1,
    minZoom: 0.5,
    maxZoom: 3,
    defaultImage: "/maps/floor1.png",
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (settings) {
      setLocalSettings({
        endpoint: settings.endpoint || "http://localhost:3051/api/devices",
        pollInterval: settings.pollInterval || 5000,
        defaultZoom: settings.defaultZoom || 1,
        minZoom: settings.minZoom || 0.5,
        maxZoom: settings.maxZoom || 3,
        defaultImage: settings.defaultImage || "/maps/floor1.png",
      });
    }
  }, [settings]);

  const handleSettingChange = (key: string, value: any) => {
    setLocalSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
    setHasChanges(true);
  };

  const handleSaveSettings = async () => {
    try {
      await dispatch(updateSettings(localSettings));
      setHasChanges(false);
    } catch (error) {
      console.error("Failed to save settings:", error);
    }
  };

  const handleResetSettings = () => {
    if (settings) {
      setLocalSettings({
        endpoint: settings.endpoint || "http://localhost:3051/api/devices",
        pollInterval: settings.pollInterval || 5000,
        defaultZoom: settings.defaultZoom || 1,
        minZoom: settings.minZoom || 0.5,
        maxZoom: settings.maxZoom || 3,
        defaultImage: settings.defaultImage || "/maps/floor1.png",
      });
      setHasChanges(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <h3 className="text-lg font-medium text-gray-900">
          {language === "he" ? "הגדרות" : "Settings"}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {language === "he"
            ? "התאם את הגדרות האפליקציה והמפה"
            : "Customize app and map settings"}
        </p>
      </div>

      <div className="flex-1">
        {/* App Settings */}
        <div className="p-4 border-b border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-3">
            {language === "he" ? "הגדרות אפליקציה" : "App Settings"}
          </h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {language === "he" ? "ערכת נושא" : "Theme"}
              </label>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => dispatch(setTheme("light"))}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    theme === "light"
                      ? "bg-blue-100 text-blue-800 border-2 border-blue-300"
                      : "bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200"
                  }`}
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                  {language === "he" ? "בהיר" : "Light"}
                </button>
                <button
                  onClick={() => dispatch(setTheme("dark"))}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    theme === "dark"
                      ? "bg-blue-100 text-blue-800 border-2 border-blue-300"
                      : "bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200"
                  }`}
                >
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
                    />
                  </svg>
                  {language === "he" ? "כהה" : "Dark"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Map Settings */}
        <div className="p-4 border-b border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-3">
            {language === "he" ? "הגדרות מפה" : "Map Settings"}
          </h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {language === "he"
                  ? "תמונת מפה ברירת מחדל"
                  : "Default Map Image"}
              </label>
              <select
                value={localSettings.defaultImage}
                onChange={(e) =>
                  handleSettingChange("defaultImage", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm mb-2"
              >
                <option value="/maps/floor1.png">Floor 1 (PNG)</option>
                <option value="/maps/floor1.svg">Floor 1 (SVG)</option>
                <option value="/maps/floor1-2.png">Floor 1-2 (PNG)</option>
              </select>
              <div className="mt-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === "he"
                    ? "העלה תמונת מפה חדשה"
                    : "Upload New Map Image"}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      // For now, just show an alert - in a real app you'd upload to server
                      alert(
                        language === "he"
                          ? `קובץ נבחר: ${file.name}. תכונה זו תהיה זמינה בקרוב.`
                          : `File selected: ${file.name}. This feature will be available soon.`
                      );
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {language === "he"
                    ? "תמיכה בפורמטים: PNG, JPG, SVG"
                    : "Supported formats: PNG, JPG, SVG"}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === "he" ? "זום ברירת מחדל" : "Default Zoom"}
                </label>
                <input
                  type="number"
                  min="0.1"
                  max="5"
                  step="0.1"
                  value={localSettings.defaultZoom}
                  onChange={(e) =>
                    handleSettingChange(
                      "defaultZoom",
                      parseFloat(e.target.value)
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === "he" ? "זום מינימלי" : "Min Zoom"}
                </label>
                <input
                  type="number"
                  min="0.1"
                  max="2"
                  step="0.1"
                  value={localSettings.minZoom}
                  onChange={(e) =>
                    handleSettingChange("minZoom", parseFloat(e.target.value))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === "he" ? "זום מקסימלי" : "Max Zoom"}
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  step="0.1"
                  value={localSettings.maxZoom}
                  onChange={(e) =>
                    handleSettingChange("maxZoom", parseFloat(e.target.value))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Data Settings */}
        <div className="p-4 border-b border-gray-200">
          <h4 className="text-md font-medium text-gray-900 mb-3">
            {language === "he" ? "הגדרות נתונים" : "Data Settings"}
          </h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {language === "he"
                  ? "תדירות עדכון נתונים"
                  : "Data Refresh Rate"}
              </label>
              <select
                value={localSettings.pollInterval}
                onChange={(e) =>
                  handleSettingChange("pollInterval", parseInt(e.target.value))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value={1000}>
                  {language === "he" ? "שנייה אחת" : "1 second"}
                </option>
                <option value={2000}>
                  {language === "he" ? "2 שניות" : "2 seconds"}
                </option>
                <option value={5000}>
                  {language === "he" ? "5 שניות" : "5 seconds"}
                </option>
                <option value={10000}>
                  {language === "he" ? "10 שניות" : "10 seconds"}
                </option>
                <option value={30000}>
                  {language === "he" ? "30 שניות" : "30 seconds"}
                </option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {language === "he"
                  ? "תדירות גבוהה יותר עלולה להשפיע על ביצועים"
                  : "Higher frequency may impact performance"}
              </p>
            </div>
          </div>
        </div>

        {/* Display Settings */}
        <div className="p-4">
          <h4 className="text-md font-medium text-gray-900 mb-3">
            {language === "he" ? "הגדרות תצוגה" : "Display Settings"}
          </h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                {language === "he"
                  ? "הצג כלי עזר בהפעלה"
                  : "Show tooltips on startup"}
              </label>
              <input
                type="checkbox"
                defaultChecked={true}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                {language === "he"
                  ? "הצג התראות מערכת"
                  : "Show system notifications"}
              </label>
              <input
                type="checkbox"
                defaultChecked={true}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                {language === "he" ? "זכור מיקום מפה" : "Remember map position"}
              </label>
              <input
                type="checkbox"
                defaultChecked={true}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save/Reset Actions */}
      {hasChanges && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex space-x-3 rtl:space-x-reverse">
            <button
              onClick={handleSaveSettings}
              disabled={loading}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center justify-center"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              ) : (
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
              {language === "he" ? "שמור שינויים" : "Save Changes"}
            </button>
            <button
              onClick={handleResetSettings}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              {language === "he" ? "בטל שינויים" : "Reset Changes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPanel;
